import React, { useEffect, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';

import { useDispatch, useSelector } from 'react-redux';
import { getUserChatMessages, getUserChats, sendMessage } from '../../../redux/ChatSlice/chatSlice';
import { checkLinkedInConnectionStatus, sendFirstLiMessage, fetchMyInviteStatuses, fetchAllConnections } from '../../../redux/LIAuthSlice/liAuthSlice';

import Sidebar from '../../../components/Navbar/SideNavbar';
import ConnectLinkedInHero from '../../../components/common/ConnectLinkedInHero';
import Navbaar from '../../../components/Navbar/Navbaar';
import { isUsablePhoto } from '../../../components/NetworkingPage/NetContent/NetPipelineRow';
import { useNavbarActions, useNavbarTitle } from '../../../context/NavbarActionsContext';

import SearchIcon from '@mui/icons-material/Search';

// ── helpers ──────────────────────────────────────────────────────────
const initials = (name) => {
    if (!name) return '?';
    const parts = String(name).trim().split(' ').filter(Boolean);
    return (parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2)).toUpperCase();
};
const gradientFor = (name) => {
    const COLORS = ['#3F57E0', '#0F172A', '#1F4DBE', '#5B21B6', '#7C2D12', '#9333EA', '#1B6E47', '#C2410C'];
    let h = 0;
    for (let i = 0; i < String(name || '').length; i++) h = String(name).charCodeAt(i) + ((h << 5) - h);
    return COLORS[Math.abs(h) % COLORS.length];
};
const LinkedInIconLink = ({ url, className = '' }) => url ? (
    <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        title="View LinkedIn profile"
        className={`w-7 h-7 grid place-items-center rounded-lg text-[#0A66C2] hover:bg-[#EEF3FF] shrink-0 transition-colors ${className}`}
    >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" /></svg>
    </a>
) : null;

const Avatar = ({ name, photo, size = 32 }) => {
    const [imgFailed, setImgFailed] = useState(false);
    if (isUsablePhoto(photo) && !imgFailed) {
        return <img src={photo} alt={name || ''} className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} onError={() => setImgFailed(true)} />;
    }
    // Fallback (no photo, or the photo failed to load): show initials so the
    // avatar never collapses to blank — e.g. "You" when user.photo is broken.
    return (
        <div className="rounded-full grid place-items-center text-white font-bold flex-shrink-0 select-none"
            style={{ width: size, height: size, fontSize: size * 0.36, background: `linear-gradient(135deg, ${gradientFor(name)}, #6377F0)` }}>
            {initials(name)}
        </div>
    );
};
const relativeTime = (ts) => {
    if (!ts) return '';
    const then = new Date(ts).getTime();
    if (Number.isNaN(then)) return '';
    const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (s < 60) return 'now';
    const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24); if (d === 1) return 'Yesterday';
    if (d < 7) return `${d}d ago`;
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ── skeleton loaders ─────────────────────────────────────────────────
const ThreadRowSkeleton = ({ last }) => (
    <div className="px-4 py-3.5 flex items-center gap-3 animate-pulse" style={{ borderBottom: last ? 'none' : '1px solid #EFF1F5' }}>
        <div className="w-10 h-10 rounded-full bg-[#EAECF2] shrink-0" />
        <div className="flex-1 space-y-2">
            <div className="h-3 w-32 bg-[#EAECF2] rounded" />
            <div className="h-2.5 w-40 bg-[#F0F1F5] rounded" />
            <div className="h-2.5 w-24 bg-[#F0F1F5] rounded" />
        </div>
    </div>
);
const ThreadListSkeleton = () => (
    <div>{[0, 1, 2, 3, 4, 5].map(i => <ThreadRowSkeleton key={i} last={i === 5} />)}</div>
);
const MessageSkeleton = ({ mine }) => (
    <div className="flex gap-3 animate-pulse">
        <div className="w-[30px] h-[30px] rounded-full bg-[#EAECF2] shrink-0" />
        <div className="flex-1 space-y-2">
            <div className="h-2.5 w-28 bg-[#EAECF2] rounded" />
            <div className={`h-2.5 ${mine ? 'w-2/3' : 'w-5/6'} bg-[#F0F1F5] rounded`} />
            <div className={`h-2.5 ${mine ? 'w-1/3' : 'w-1/2'} bg-[#F0F1F5] rounded`} />
        </div>
    </div>
);

const Inbox = () => {
    const dispatch = useDispatch();
    const messagesEndRef = useRef(null);

    const user = useSelector(state => state.auth.user);
    const { isLinkedInConnected, connections, connectionsLoading, connectionChecked } = useSelector((state) => state.liauth);
    const allMessages = useSelector((state) => state.chats.chatMessages);
    const { chats, chatsLoading, chatMessages, chatMessagesLoading } = useSelector((state) => state.chats);

    const [selectedChat, setSelectedChat] = useState(null);
    const [text, setText] = useState("");
    const [newMessageText, setNewMessageText] = useState('');
    const [optimisticFirstMessagesByProviderId, setOptimisticFirstMessagesByProviderId] = useState({});
    const [toast, setToast] = useState(null);
    const [searchText, setSearchText] = useState('');

    const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

    useEffect(() => {
        if (user?._id) {
            dispatch(checkLinkedInConnectionStatus());
            dispatch(fetchMyInviteStatuses());
        }
    }, [dispatch, user?._id]);

    useEffect(() => {
        if (isLinkedInConnected?.accountId && chats.length === 0) {
            dispatch(getUserChats({ accountId: isLinkedInConnected?.accountId }))
        }
    }, [dispatch, isLinkedInConnected?.accountId, chats.length])

    // Load connections via the bulk /fetchconnections endpoint (same as Feed).
    useEffect(() => {
        if (isLinkedInConnected?.accountId && connections.length === 0) {
            dispatch(fetchAllConnections({ accountId: isLinkedInConnected.accountId }));
        }
    }, [dispatch, isLinkedInConnected?.accountId, connections.length]);

    useEffect(() => {
        if (selectedChat?.chatId && !chatMessages[selectedChat.chatId]) {
            dispatch(getUserChatMessages({ chatId: selectedChat.chatId }));
        }
    }, [selectedChat, dispatch, chatMessages]);

    // Poll messages while a real chat is open (quick "real-time" feel).
    useEffect(() => {
        const chatId = selectedChat?.chatId;
        if (!chatId) return;
        const interval = setInterval(() => {
            dispatch(getUserChatMessages({ chatId }));
        }, 7000);
        return () => clearInterval(interval);
    }, [dispatch, selectedChat?.chatId]);

    const optimisticMessagesForSuggested = selectedChat?.isSuggested
        ? (optimisticFirstMessagesByProviderId[selectedChat?.providerId] || [])
        : [];
    const currentMessages = selectedChat?.isSuggested
        ? optimisticMessagesForSuggested
        : (allMessages[selectedChat?.chatId] || []);

    const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); };
    useEffect(() => { scrollToBottom(); }, [currentMessages]);

    const handleSendMessage = () => {
        if (!selectedChat || !text.trim()) return;
        dispatch(sendMessage({ accountId: selectedChat.accountId, text: text, chatId: selectedChat.chatId }));
        setText("");
    };

    const handleSendFirstMessageToConnection = async () => {
        if (!selectedChat?.isSuggested) return;
        const aid = isLinkedInConnected?.accountId;
        const attendeeId = selectedChat?.providerId;
        if (!aid || !attendeeId || !newMessageText.trim() || !user?._id) return;

        const payloadText = newMessageText;
        setOptimisticFirstMessagesByProviderId((prev) => ({
            ...prev,
            [attendeeId]: [
                ...(prev[attendeeId] || []),
                { messageId: `optimistic-${Date.now()}`, text: payloadText, isSender: true }
            ]
        }));
        setNewMessageText('');

        try {
            await dispatch(sendFirstLiMessage({ accountId: aid, attendeeId, text: payloadText, userId: user._id })).unwrap();
            const nextChats = await dispatch(getUserChats({ accountId: aid })).unwrap();
            const nextChat = (Array.isArray(nextChats) ? nextChats : []).find((c) =>
                c?.attendees?.some((a) => String(a?.providerId || '') === String(attendeeId))
            );
            if (nextChat) {
                const attendee = nextChat.attendees?.find((a) => String(a?.providerId || '') === String(attendeeId)) || nextChat.attendees?.[0];
                setSelectedChat({ ...(attendee || {}), chatId: nextChat.chatId, accountId: nextChat.accountId, isSuggested: false });
            }
        } catch (_) { /* keep optimistic bubble */ }
    };

    const unreadCount = chats.filter(c => c.unread).length;

    // Search filter — matches contact name, role (title) and company.
    const q = searchText.trim().toLowerCase();
    const matchesQuery = (...fields) => !q || fields.some(f => String(f || '').toLowerCase().includes(q));
    const filteredChats = chats.filter(c => {
        const a = c.attendees?.[0] || {};
        return matchesQuery(a.name, a.title, a.organization_name);
    });
    const filteredConnections = connections.filter(c => matchesQuery(c.name, c.title, c.organization_name));

    // Connections that don't already have a conversation — shown below the chats
    // so the user can start a new conversation with any of them.
    const chatProviderIds = new Set(
        chats.flatMap(c => (c.attendees || []).map(a => String(a?.providerId || ''))).filter(Boolean)
    );
    const suggestedConnections = filteredConnections.filter(c => !chatProviderIds.has(String(c.providerId || '')));

    // ── navbar title + actions (design's Frame header) ──────────────────
    useNavbarTitle(
        { title: 'Inbox', subtitle: `${unreadCount > 0 ? `${unreadCount} unread · ` : ''}${chats.length} active conversations` },
        [unreadCount, chats.length]
    );
    useNavbarActions(
        isLinkedInConnected?.connected ? (
            <button
                onClick={() => showToast('Mark all read — coming soon')}
                className="inline-flex items-center px-3 py-1.5 rounded-lg text-[12px] font-semibold text-[#334155] bg-white border border-[#E6E8EE] hover:border-[#D6DAE3] transition-colors"
            >
                Mark all read
            </button>
        ) : null,
        [isLinkedInConnected?.connected]
    );

    return (
        <div className="relative bg-[#F6F7FB] h-screen pt-16 flex flex-col">
            <Navbaar />
            <div className="flex flex-1 min-h-0">
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar />
                </div>

                {connectionChecked && !isLinkedInConnected?.connected ? (
                    <ConnectLinkedInHero />
                ) : (
                <div className="flex-1 min-h-0 p-6">
                    <div className="grid h-full min-h-0" style={{ gridTemplateColumns: '360px 1fr', gap: 18 }}>

                        {/* ── Thread list ─────────────────────────────── */}
                        <div className="bg-white rounded-xl border border-[#E6E8EE] flex flex-col overflow-hidden" style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}>
                            <div className="px-3.5 py-3 border-b border-[#EFF1F5]">
                                <div className="flex items-center gap-2 bg-[#F1F3F7] rounded-[10px] px-2.5 py-1.5 border border-transparent focus-within:bg-white focus-within:border-[#E0E5FC]">
                                    <SearchIcon style={{ fontSize: 14, color: '#94A3B8' }} />
                                    <input type="text" value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Search inbox…" className="flex-1 text-[12.5px] bg-transparent outline-none text-[#0F172A] placeholder-[#94A3B8]" />
                                </div>
                            </div>

                            <div className="flex-1 overflow-y-auto">
                                {(chatsLoading && chats.length === 0) || (connectionsLoading && connections.length === 0 && chats.length === 0) ? (
                                    <ThreadListSkeleton />
                                ) : chats.length === 0 && connections.length === 0 ? (
                                    <div className="p-6 text-center">
                                        <p className="text-[13px] font-semibold text-[#0F172A]">No connected contacts yet</p>
                                        <p className="text-[11.5px] text-[#64748B] mt-1">Platform contacts who accept your invite will appear here to message.</p>
                                    </div>
                                ) : filteredChats.length === 0 && suggestedConnections.length === 0 ? (
                                    <div className="p-6 text-center text-[12.5px] text-[#94A3B8]">No conversations or contacts match "{searchText}".</div>
                                ) : (
                                    <>
                                        {/* Existing conversations */}
                                        {filteredChats.map((chat) => {
                                            const attendee = chat.attendees?.[0];
                                            if (!attendee) return null;
                                            const isActive = selectedChat?.chatId === chat.chatId;
                                            return (
                                                <div
                                                    key={chat._id}
                                                    onClick={() => setSelectedChat({ profile_picture: attendee.profile_picture, name: attendee.name, title: attendee.title, organization_name: attendee.organization_name, linkedin_url: attendee.linkedin_url, chatId: chat.chatId, accountId: chat.accountId })}
                                                    className="px-4 py-3 cursor-pointer transition-colors border-b border-[#EFF1F5]"
                                                    style={{ background: isActive ? '#EEF1FE' : 'transparent', borderLeft: isActive ? '3px solid #3F57E0' : '3px solid transparent' }}
                                                >
                                                    <div className="flex items-center gap-2.5">
                                                        <Avatar name={attendee.name} photo={attendee.profile_picture} size={32} />
                                                        <div className="flex-1 min-w-0 text-left">
                                                            <div className="flex items-center gap-1.5">
                                                                <p className="truncate flex-1 text-left text-[12.5px] text-[#0F172A]" style={{ fontWeight: chat.unread ? 700 : 600 }}>{attendee.name}</p>
                                                                {chat.unread && <span className="w-1.5 h-1.5 rounded-full bg-[#3F57E0] shrink-0" />}
                                                                <span className="text-[10.5px] text-[#94A3B8] shrink-0">{relativeTime(chat.lastMessageTimestamp)}</span>
                                                            </div>
                                                            <p className="text-[11px] text-[#94A3B8] truncate mt-0.5 text-left">{attendee.organization_name || attendee.title}</p>
                                                        </div>
                                                        <LinkedInIconLink url={attendee.linkedin_url} />
                                                    </div>
                                                    {chat.lastMessage && (
                                                        <p className="text-[12px] text-[#64748B] mt-2 text-left line-clamp-2">{chat.lastMessage}</p>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {/* Connections without a conversation yet */}
                                        {suggestedConnections.length > 0 && (
                                            <div className="px-4 pt-3 pb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-[#94A3B8]">Start a conversation</div>
                                        )}
                                        {suggestedConnections.map((connection) => {
                                            const isActive = selectedChat?.isSuggested && (selectedChat?.providerId === connection.providerId || selectedChat?._id === connection._id);
                                            return (
                                                <div
                                                    key={connection._id || connection.providerId}
                                                    onClick={() => setSelectedChat({ ...connection, chatId: null, isSuggested: true, connectionStatus: 'accepted', accountId: isLinkedInConnected?.accountId })}
                                                    className="px-4 py-3.5 cursor-pointer transition-colors flex items-center gap-3 border-b border-[#EFF1F5]"
                                                    style={{ background: isActive ? '#EEF1FE' : 'transparent', borderLeft: isActive ? '3px solid #3F57E0' : '3px solid transparent' }}
                                                >
                                                    <Avatar name={connection.name} photo={connection.profile_picture} size={40} />
                                                    <div className="flex-1 min-w-0 text-left">
                                                        <p className="text-[13px] font-semibold text-[#0F172A] truncate text-left">{connection.name || 'LinkedIn Member'}</p>
                                                        {connection.title && <p className="text-[12px] text-[#64748B] truncate mt-0.5 text-left">{connection.title}</p>}
                                                        {connection.organization_name && <p className="text-[11px] text-[#94A3B8] truncate mt-0.5 text-left">{connection.organization_name}</p>}
                                                    </div>
                                                    <LinkedInIconLink url={connection.linkedin_url} />
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* ── Thread view ─────────────────────────────── */}
                        <div className="bg-white rounded-xl border border-[#E6E8EE] flex flex-col min-h-0 overflow-hidden" style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}>
                            {selectedChat ? (
                                <>
                                    {/* header */}
                                    <div className="flex items-center justify-between px-5 py-4 border-b border-[#EFF1F5] shrink-0">
                                        <div className="flex items-center gap-3">
                                            <Avatar name={selectedChat.name} photo={selectedChat.profile_picture} size={40} />
                                            <div className="text-left">
                                                <p className="text-[14.5px] font-bold text-[#0F172A] text-left">{selectedChat.name} {selectedChat.organization_name && <span className="font-normal text-[#64748B]">· {selectedChat.organization_name}</span>}</p>
                                                {selectedChat.title && <p className="text-[11.5px] text-[#64748B] mt-0.5 text-left">{selectedChat.title}</p>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                onClick={() => showToast('Schedule call — coming soon')}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E6E8EE] text-[12px] font-semibold text-[#334155] hover:border-[#D6DAE3] transition-colors"
                                            >
                                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
                                                Schedule call
                                            </button>
                                        </div>
                                    </div>

                                    {/* messages */}
                                    <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
                                        {selectedChat.isSuggested ? (
                                            selectedChat.connectionStatus === 'pending' ? (
                                                <div className="flex flex-col items-center justify-center h-full text-center">
                                                    <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100 max-w-sm">
                                                        <p className="text-[13.5px] font-bold text-[#0F172A] mb-2">Invitation Pending</p>
                                                        <p className="text-[12.5px] text-[#64748B]">You've sent a connection request to {selectedChat.name}. Once they accept on LinkedIn, you'll be able to message them here.</p>
                                                        {selectedChat.linkedin_url && (
                                                            <a href={selectedChat.linkedin_url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block text-[#0A66C2] font-medium text-[12.5px] hover:underline">View LinkedIn Profile →</a>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : currentMessages.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center h-full text-center px-6">
                                                    <div className="relative mb-5">
                                                        <Avatar name={selectedChat.name} photo={selectedChat.profile_picture} size={72} />
                                                        <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-emerald-500 border-[3px] border-white grid place-items-center" style={{ boxShadow: '0 2px 6px rgba(16,185,129,.4)' }}>
                                                            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                                        </span>
                                                    </div>
                                                    <p className="text-[16px] font-extrabold text-[#0F172A]">You're connected with {String(selectedChat.name || '').split(' ')[0] || 'them'} 🎉</p>
                                                    <p className="text-[12.5px] text-[#64748B] mt-2 max-w-[300px] leading-relaxed">Send your first message to start the conversation — a short, specific opener gets the best response.</p>
                                                    <div className="mt-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F1F3F7] text-[11.5px] font-medium text-[#64748B]">
                                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
                                                        Type your message below
                                                    </div>
                                                </div>
                                            ) : (
                                                currentMessages.slice().reverse().map((message) => (
                                                    <ThreadMessage key={message.messageId} message={message} selectedChat={selectedChat} user={user} />
                                                ))
                                            )
                                        ) : chatMessagesLoading && currentMessages.length === 0 ? (
                                            <>
                                                <MessageSkeleton />
                                                <MessageSkeleton mine />
                                                <MessageSkeleton />
                                            </>
                                        ) : (
                                            currentMessages.slice().reverse().map((message) => (
                                                <ThreadMessage key={message.messageId} message={message} selectedChat={selectedChat} user={user} />
                                            ))
                                        )}
                                        <div ref={messagesEndRef} />
                                    </div>

                                    {/* compose */}
                                    {(selectedChat.isSuggested && selectedChat.connectionStatus !== 'pending') || !selectedChat.isSuggested ? (
                                        <div className="border-t border-[#EFF1F5] px-4 py-3 bg-[#FAFBFD] shrink-0">
                                            <div className="bg-white border border-[#E6E8EE] rounded-xl px-3 py-2.5">
                                                <TextareaAutosize
                                                    className="w-full text-[13px] bg-transparent outline-none resize-none text-[#0F172A] placeholder-[#94A3B8]"
                                                    placeholder="Type your reply…"
                                                    minRows={3}
                                                    maxRows={6}
                                                    value={selectedChat.isSuggested ? newMessageText : text}
                                                    onChange={(e) => selectedChat.isSuggested ? setNewMessageText(e.target.value) : setText(e.target.value)}
                                                />
                                                <div className="flex items-center justify-end mt-1.5">
                                                    <button
                                                        onClick={selectedChat.isSuggested ? handleSendFirstMessageToConnection : handleSendMessage}
                                                        className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#3F57E0] hover:bg-[#3347C8] text-white text-[12px] font-semibold rounded-lg transition-colors"
                                                    >
                                                        Send
                                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                </>
                            ) : (
                                <div className="flex-1 flex items-center justify-center">
                                    <div className="text-center">
                                        <div className="w-12 h-12 rounded-2xl bg-[#EEF1FE] flex items-center justify-center mx-auto mb-3">
                                            <svg className="w-6 h-6 text-[#3F57E0]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                                        </div>
                                        <p className="text-[13px] font-bold text-[#0F172A]">Select a conversation</p>
                                        <p className="text-[11.5px] text-[#64748B] mt-1">Choose a thread from the list to start messaging.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                )}
            </div>
            {toast && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2.5 rounded-xl bg-gray-900 text-white text-[13px] font-medium shadow-lg z-50">
                    {toast}
                </div>
            )}
        </div>
    );
};

// Threaded message row (design style: avatar + name · time + body, left-aligned).
const ThreadMessage = ({ message, selectedChat, user }) => {
    const mine = message.isSender;
    const name = mine ? (user?.name || 'You') : (selectedChat?.name || 'LinkedIn Member');
    const photo = mine ? user?.photo : selectedChat?.profile_picture;
    const when = relativeTime(message.timestamp || message.date || message.createdAt);
    return (
        <div className="flex gap-3">
            <Avatar name={name} photo={photo} size={30} />
            <div className="flex-1 min-w-0">
                <div className="text-[12px] font-semibold text-[#0F172A] text-left">
                    {mine ? 'You' : name}{when && <span className="font-normal text-[#94A3B8]"> · {when}</span>}
                </div>
                <div className="text-[12.5px] text-[#334155] leading-relaxed mt-1.5 text-left whitespace-pre-wrap break-words">{message.text}</div>
            </div>
        </div>
    );
};

export default Inbox;
